import type { ReactElement } from 'react';

/**
 * 히어로 카드의 오브젝트 일러스트 — 레퍼런스의 3D 캐릭터 자리.
 *
 * 사진·렌더 이미지를 받기 전까지 쓰는 벡터 오브젝트다. 그라데이션과 그림자로 부피를 만들어
 * 평면 아이콘처럼 보이지 않게 한다. 카드마다 한 번씩만 그려지므로 gradient id 가 겹치지 않는다.
 * 색은 토큰(민트·잉크)만 쓴다 — 카드 면이 이미 민트·잉크·민트 소프트라 새 색을 들이면 흩어진다.
 */

const SHADOW_INK = '#06110B';
const PAPER_TOP = '#FFFFFF';
const PAPER_BOTTOM = '#E6F3EC';

type ArtProps = { className?: string };

function DropShadow({ id, opacity }: { id: string; opacity: number }): ReactElement {
  return (
    <filter id={id} x="-40%" y="-40%" width="180%" height="180%">
      <feDropShadow
        dx="0"
        dy="12"
        stdDeviation="11"
        floodColor={SHADOW_INK}
        floodOpacity={opacity}
      />
    </filter>
  );
}

/** 무료 자료실 — 겹친 문서 더미와 받기 버튼 */
export function LibraryArt({ className }: ArtProps): ReactElement {
  return (
    <svg viewBox="0 0 260 220" aria-hidden="true" className={className}>
      <defs>
        <linearGradient id="hero-lib-paper" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={PAPER_TOP} />
          <stop offset="1" stopColor={PAPER_BOTTOM} />
        </linearGradient>
        <radialGradient id="hero-lib-badge" cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#2A332E" />
          <stop offset="1" stopColor="#0B0F0D" />
        </radialGradient>
        <DropShadow id="hero-lib-shadow" opacity={0.22} />
      </defs>

      <g filter="url(#hero-lib-shadow)">
        <rect
          x="86"
          y="14"
          width="124"
          height="158"
          rx="16"
          transform="rotate(13 148 93)"
          fill="url(#hero-lib-paper)"
          opacity="0.75"
        />
        <g transform="rotate(-7 118 104)">
          <rect x="56" y="24" width="124" height="158" rx="16" fill="url(#hero-lib-paper)" />
          <rect x="74" y="44" width="58" height="12" rx="4" className="fill-primary" />
          {[70, 86, 102, 118].map((y, index) => (
            <rect
              key={y}
              x="74"
              y={y}
              width={index === 3 ? 52 : 88}
              height="7"
              rx="3.5"
              fill={SHADOW_INK}
              opacity="0.12"
            />
          ))}
        </g>
      </g>

      <g filter="url(#hero-lib-shadow)">
        <circle cx="190" cy="160" r="40" fill="url(#hero-lib-badge)" />
        <path
          d="M190 139v34M175 159l15 15 15-15"
          fill="none"
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="stroke-primary"
        />
      </g>
    </svg>
  );
}

/** 프로필 동그라미 하나 — 머리와 어깨만 남긴 사람 모양 */
function Avatar({ cx, cy, fill }: { cx: number; cy: number; fill: string }): ReactElement {
  return (
    <g>
      <circle cx={cx} cy={cy} r="34" fill={fill} stroke="#0B0F0D" strokeWidth="5" />
      <circle cx={cx} cy={cy - 9} r="11" fill={SHADOW_INK} opacity="0.78" />
      <path
        d={`M${cx - 19} ${cy + 24}c0-11 8.5-19 19-19s19 8 19 19`}
        fill={SHADOW_INK}
        opacity="0.78"
      />
    </g>
  );
}

/** 커뮤니티 — 모여 있는 사람들과 오가는 반응 */
export function CommunityArt({ className }: ArtProps): ReactElement {
  return (
    <svg viewBox="0 0 260 220" aria-hidden="true" className={className}>
      <defs>
        <radialGradient id="hero-com-mint" cx="0.35" cy="0.3" r="0.85">
          <stop offset="0" stopColor="#B6FED9" />
          <stop offset="1" stopColor="#6CFDB3" />
        </radialGradient>
        <radialGradient id="hero-com-paper" cx="0.35" cy="0.3" r="0.85">
          <stop offset="0" stopColor={PAPER_TOP} />
          <stop offset="1" stopColor={PAPER_BOTTOM} />
        </radialGradient>
        <radialGradient id="hero-com-soft" cx="0.35" cy="0.3" r="0.85">
          <stop offset="0" stopColor="#E4FFF1" />
          <stop offset="1" stopColor="#B6FED9" />
        </radialGradient>
        <DropShadow id="hero-com-shadow" opacity={0.55} />
      </defs>

      {/* 가운데 사람이 앞에 오도록 양옆을 먼저 그린다 */}
      <g filter="url(#hero-com-shadow)">
        <Avatar cx={74} cy={158} fill="url(#hero-com-paper)" />
        <Avatar cx={186} cy={158} fill="url(#hero-com-soft)" />
        <Avatar cx={130} cy={146} fill="url(#hero-com-mint)" />
      </g>

      <g filter="url(#hero-com-shadow)" transform="rotate(-6 70 64)">
        <path
          d="M40 40h62a22 22 0 0 1 22 22v2a22 22 0 0 1-22 22H66l-16 14 3-14h-13a22 22 0 0 1-22-22v-2a22 22 0 0 1 22-22z"
          fill="url(#hero-com-paper)"
        />
        {[54, 71, 88].map((cx) => (
          <circle key={cx} cx={cx} cy="63" r="6" fill={SHADOW_INK} opacity="0.3" />
        ))}
      </g>

      <g filter="url(#hero-com-shadow)" transform="rotate(8 196 56)">
        <path
          d="M172 28h48a20 20 0 0 1 20 20v8a20 20 0 0 1-20 20h-8l3 14-17-14h-26a20 20 0 0 1-20-20v-8a20 20 0 0 1 20-20z"
          fill="url(#hero-com-mint)"
        />
        <path
          d="M196 66c-10-6.5-15-12-15-18a7.5 7.5 0 0 1 15-2.5 7.5 7.5 0 0 1 15 2.5c0 6-5 11.5-15 18z"
          fill={SHADOW_INK}
        />
      </g>
    </svg>
  );
}

/** 기업 도입 문의 — 질문과 답이 오가는 말풍선 */
export function InquiryArt({ className }: ArtProps): ReactElement {
  return (
    <svg viewBox="0 0 260 220" aria-hidden="true" className={className}>
      <defs>
        <linearGradient id="hero-inq-bubble" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={PAPER_TOP} />
          <stop offset="1" stopColor={PAPER_BOTTOM} />
        </linearGradient>
        <radialGradient id="hero-inq-ink" cx="0.3" cy="0.25" r="0.9">
          <stop offset="0" stopColor="#2A332E" />
          <stop offset="1" stopColor="#0B0F0D" />
        </radialGradient>
        <DropShadow id="hero-inq-shadow" opacity={0.2} />
      </defs>

      <g filter="url(#hero-inq-shadow)" transform="rotate(-6 100 76)">
        <path
          d="M40 40h120a30 30 0 0 1 30 30v4a30 30 0 0 1-30 30H78l-22 18 4-18h-20a30 30 0 0 1-30-30v-4a30 30 0 0 1 30-30z"
          fill="url(#hero-inq-bubble)"
        />
        {[76, 100, 124].map((cx) => (
          <circle key={cx} cx={cx} cy="72" r="7" fill={SHADOW_INK} opacity="0.28" />
        ))}
      </g>

      <g filter="url(#hero-inq-shadow)" transform="rotate(5 170 156)">
        <path
          d="M110 118h110a28 28 0 0 1 28 28v8a28 28 0 0 1-28 28h-6l4 20-24-20h-84a28 28 0 0 1-28-28v-8a28 28 0 0 1 28-28z"
          fill="url(#hero-inq-ink)"
        />
        <circle cx="120" cy="150" r="17" className="fill-primary" />
        <path
          d="M112 150l6 6 11-12"
          fill="none"
          stroke={SHADOW_INK}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="148" y="140" width="72" height="7" rx="3.5" fill="#FFFFFF" opacity="0.55" />
        <rect x="148" y="155" width="46" height="7" rx="3.5" fill="#FFFFFF" opacity="0.3" />
      </g>
    </svg>
  );
}
